/**
 * contentValidators/notationChecks.ts
 * Validates notation requirements: LaTeX fractions, comma decimals,
 * euro notation, and superscript units.
 */

import type { ValidationIssue } from '../../issueContract';
import { coach } from '../../issueContract';
import { getNotationRequirements, isPolicyFeatureEnabled } from '../../policyEngine';
import type { Question } from '../../../../content';
import { STAGE } from './shared';
import { SEVERITY } from '../../severityScale';

/**
 * Strip Dutch thousand-separator dots from a text.
 *
 * The naive single-pass regex /\d\.\d{3}(?!\d)/g only matches ONE
 * separator per number, so chained thousand-separators like "5.000.000"
 * collapse to "5000.000" which then trips the decimal-dot check (false
 * positive — surfaced juni 2026 on GET-VERH-SCHAAL-KAART-003).
 *
 * Loop the replacement until the text stabilises so every separator is
 * stripped. The number of separators per token is small (Dutch numbers
 * are at most billions), so the loop terminates in ≤ 3 iterations.
 */
function stripDutchThousandSeparators(text: string): string {
  let current = text;
  let prev = '';
  while (prev !== current) {
    prev = current;
    current = current.replace(/(\d)\.(\d{3})(?!\d)/g, '$1$2');
  }
  return current;
}

/**
 * Validate notation requirements for a question.
 */
export function validateNotation(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const qId = question.id ?? 'UNKNOWN';
  const fId = question.familyId ?? 'UNKNOWN';
  const module = question.module;
  if (!module) return issues;
  const notation = getNotationRequirements(module);
  const fullText = (question.question ?? '') + ' ' + (question.options?.join(' ') ?? '');

  // LaTeX fractions
  if (notation.requireLatexFractions && isPolicyFeatureEnabled('enforceLatex')) {
    const isFractionContext = fId.includes('BREUK') || (question.skill ?? '').toLowerCase().includes('breuk');
    if (isFractionContext && fullText.includes('/') && !fullText.includes('\\frac')) {
      issues.push(coach('content', STAGE, 'CONTENT_MISSING_LATEX', 'Fractions should use LaTeX (\\frac{}{}) instead of /', {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.LOW
      }));
    }
  }

  // Comma decimals
  if (notation.requireCommaDecimals) {
    // Strip Dutch thousand-separator dots before checking for decimal dots.
    const textForDotCheck = stripDutchThousandSeparators(fullText);
    if (textForDotCheck.match(/\d+\.\d+/) && !fId.includes('WETENSCHAPPELIJK')) {
      issues.push(coach('content', STAGE, 'CONTENT_DOT_DECIMAL', 'Use comma (,) for decimals, not dot (.)', {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_MEDIUM
      }));
    }
  }

  // Euro comma
  if (notation.requireEuroComma) {
    // Strip Dutch thousand-separator dots before checking for euro-dot decimals.
    // Otherwise €1.000 (one thousand euro, Dutch convention) trips the check
    // because \d+\.\d{2} matches "1.00" inside "1.000".
    const textForEuroCheck = stripDutchThousandSeparators(fullText);
    if (textForEuroCheck.includes('€') && textForEuroCheck.match(/\d+\.\d{2}/)) {
      issues.push(coach('content', STAGE, 'CONTENT_EURO_DOT', 'Use comma (€3,50) not dot (€3.50) for euro notation', {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.NEAR_MEDIUM
      }));
    }
  }

  // Superscript units
  if (notation.requireSuperscriptUnits && isPolicyFeatureEnabled('enforceUnitNotation')) {
    const requiresSuperscript = fId.includes('OPP') || fId.includes('INH');
    if (requiresSuperscript && (fullText.includes('m2') || fullText.includes('cm2') || fullText.includes('m3'))) {
      issues.push(coach('content', STAGE, 'CONTENT_MISSING_SUPERSCRIPT', 'Use superscripts (m², cm³) instead of m2, cm3', {
        field: 'question',
        questionId: qId,
        familyId: fId,
        severity: SEVERITY.LOW
      }));
    }
  }
  return issues;
}